<script lang="ts">
  import { onMount } from 'svelte';
  import {
    Chart,
    LineController,
    LineElement,
    PointElement,
    Filler,
    LinearScale,
    CategoryScale,
  } from 'chart.js';
  import type { TimeSeriesPoint } from '../../api';

  Chart.register(LineController, LineElement, PointElement, Filler, LinearScale, CategoryScale);

  export let title: string;
  export let value: string;
  export let trend: TimeSeriesPoint[] = [];
  export let unit: string = '';
  export let trendDirection: 'up' | 'down' | 'neutral' = 'neutral';

  let canvas: HTMLCanvasElement;
  let chart: Chart | null = null;

  function createSparkline() {
    if (!canvas || trend.length < 2) return;

    if (chart) {
      chart.destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const labels = trend.map((p) => p.date);
    const data = trend.map((p) => p.value);

    chart = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            data,
            borderColor: '#e8772e',
            borderWidth: 1.5,
            fill: {
              target: 'origin',
              above: 'rgba(232, 119, 46, 0.08)',
            },
            pointRadius: 0,
            tension: 0.3,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        scales: {
          x: { display: false },
          y: {
            display: false,
            beginAtZero: true,
          },
        },
        interaction: { mode: 'nearest', intersect: false },
        animation: { duration: 300 },
      },
    });
  }

  onMount(() => {
    createSparkline();
    return () => {
      if (chart) chart.destroy();
    };
  });

  $: if (canvas && trend) {
    createSparkline();
  }
</script>

<div class="flex flex-col gap-2 p-4 bg-surface border border-border rounded-lg">
  <div class="flex items-center justify-between">
    <span class="text-xs font-medium text-text-muted uppercase tracking-wider">{title}</span>
    {#if trendDirection !== 'neutral'}
      <span class="text-xs font-medium {trendDirection === 'up' ? 'text-success' : 'text-error'}">
        {trendDirection === 'up' ? '↑' : '↓'}
      </span>
    {/if}
  </div>

  <div class="flex items-baseline gap-1.5">
    <span class="text-2xl font-semibold text-text">{value}</span>
    {#if unit}
      <span class="text-xs text-text-muted">{unit}</span>
    {/if}
  </div>

  {#if trend.length >= 2}
    <div class="h-12 mt-1">
      <canvas bind:this={canvas}></canvas>
    </div>
  {/if}
</div>
